import Select from '@components/v2/form/Select/Select';
import TextField from '@components/v2/form/TextField/TextField';
import DiscardChangesModal from '@components/v2/ui/DiscardChangesModal/DiscardChangesModal';
import Modal from '@components/v2/ui/Modal/Modal';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Fade, Grid } from '@mui/material';
import { useId } from 'react';
import { Controller } from 'react-hook-form';
import AvatarUpload from '../../components/AvatarUpload';
import BannerForm from '../../components/Site/BannerForm/BannerForm';
import SocialMediaField from '../../components/Site/SocialMediaField/SocialMediaField';
import { SOCIAL_MEDIA_FIELDS } from '../../components/Site/SocialMediaField/socialMediaField.constants';
import LangEnum from '../../lang/LangEnum';
import { BrandShowroom } from '../../services/modules/brand-showroom/brand-showroom.types';
import { isAdminTeceo } from '../context/ApplicationContext/applicationContext.utils';
import SegmentsPicker from '../segments/components/SegmentsPicker';
import { useTranslation } from '../translation/TranslationContext';
import useBrandAboutSectionForm from './hooks/useBrandAboutSectionForm';

type BrandAboutSectionFormProps = {
  isOpen: boolean;
  initialData: Partial<BrandShowroom> | undefined;
  onSave: () => void;
  onClose: () => void;
};

const BrandAboutSectionForm = ({
  isOpen,
  initialData,
  onSave,
  onClose,
}: BrandAboutSectionFormProps) => {
  const { translate } = useTranslation();
  const formId = useId();

  const {
    control,
    bannerUrl,
    logoUrl,
    segmentIds,
    segmentOptions,
    mediumPriceOptions,
    segmentsError,
    isSaving,
    isUploadingBanner,
    isUploadingLogo,
    discardModalProps,
    save,
    handleChangeBanner,
    handleChangeLogo,
    handleToggleSegment,
    handleExited,
    handleRequestClose,
  } = useBrandAboutSectionForm({ isOpen, initialData, onSave, onClose });

  return (
    <>
      <Modal
        isOpen={isOpen}
        size="registration"
        header={{ title: LangEnum.SHOWROOM_EDIT_MAIN_SECTION }}
        onClose={handleRequestClose}
        onExited={handleExited}
        footer={{
          primaryAction: {
            label: LangEnum.GLOBAL_SAVE,
            isLoading: isSaving || isUploadingBanner || isUploadingLogo,
            type: 'submit',
            form: formId,
          },
          secondaryAction: {
            label: LangEnum.GLOBAL_CANCEL_LABEL,
            onClick: handleRequestClose,
          },
        }}
      >
        <Box component="form" id={formId} noValidate onSubmit={save}>
          <Grid container spacing={2} sx={{ pt: 1.5 }}>
            <Grid size={12}>
              <Typography color="text.secondary" sx={{ mb: 1 }}>
                {translate(LangEnum.SHOWROOM_ADD_BANNER)}
                <Box component="span" sx={{ marginLeft: 1, fontStyle: 'italic' }}>
                  {translate(LangEnum.STOREFRONT_ADD_STORE_PHOTO_WIDTHS)}
                </Box>
              </Typography>
              <BannerForm
                isLoading={isUploadingBanner}
                bannerUrl={bannerUrl}
                type="showroom"
                onChange={handleChangeBanner}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 8 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 'auto' }}>
                  <Typography align="center" color="text.secondary" sx={{ mb: 1 }}>
                    {translate(LangEnum.SHOWROOM_ADD_LOGO, { br: <br /> })}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <AvatarUpload
                      alt={LangEnum.GLOBAL_LOGO}
                      imageUrl={logoUrl ?? undefined}
                      isLoading={isUploadingLogo}
                      proccessFile={handleChangeLogo}
                    />
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 'grow' }}>
                  <Grid container spacing={2}>
                    <Grid size={12}>
                      <Controller
                        name="commercialName"
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            autoFocus
                            label={translate(LangEnum.SHOWROOM_BRAND_NAME)}
                            disabled={!isAdminTeceo()}
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message}
                            testId="showroom_commercial_name"
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={12}>
                      <Controller
                        name="title"
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            label={translate(LangEnum.GLOBAL_SLOGAN)}
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message}
                            testId="showroom_title"
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={12}>
                      <Controller
                        name="website"
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            label={translate(LangEnum.GLOBAL_WEBSITE)}
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message}
                            testId="showroom_website"
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={12}>
                      <Controller
                        name="wholesaleMediumPrice"
                        control={control}
                        render={({ field, fieldState }) => (
                          <Select
                            {...field}
                            label={translate(LangEnum.SHOWROOM_AVERAGE_WHOLESALE_PRICE)}
                            options={mediumPriceOptions}
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message}
                            testId="showroom_wholesale_medium_price"
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={12}>
                  <Controller
                    name="aboutMe"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        multiline
                        rows={4}
                        label={translate(LangEnum.SHOWROOM_ABOUT)}
                        placeholder={translate(LangEnum.SHOWROOM_ABOUT_ME_PLACEHOLDER)}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                        testId="showroom_about_me"
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <Grid container spacing={2}>
                {SOCIAL_MEDIA_FIELDS.map(({ name, ...socialMedia }) => (
                  <Grid key={name} size={12}>
                    <SocialMediaField control={control} name={name} socialMedia={socialMedia} />
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Fade in={segmentOptions.length > 0 || Boolean(segmentsError)}>
              <Grid size={12}>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <Typography variant="h4" gutterBottom>
                      {translate(LangEnum.STOREFRONT_TYPES_OF_PRODUCTS)}
                    </Typography>
                    <Typography color="text.secondary">
                      {translate(LangEnum.SHOWROOM_TYPES_OF_PRODUCTS_TEXT)}
                    </Typography>
                  </Grid>
                  <Grid size={12}>
                    <SegmentsPicker
                      options={segmentOptions}
                      selectedIds={segmentIds}
                      hasError={Boolean(segmentsError)}
                      helperText={segmentsError}
                      testId="showroom_segment"
                      onToggle={handleToggleSegment}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Fade>
          </Grid>
        </Box>
      </Modal>

      <DiscardChangesModal {...discardModalProps} />
    </>
  );
};

export default BrandAboutSectionForm;
